import React, { useCallback } from 'react';
import { ChipSelector } from './ChipSelector';
import DayFirstDate from "./DayFirstDate";

// ── Chip Options ───────────────────────────────────────────────────
const GENDER_OPTIONS = [
  { value: 'male', label: 'Male' }, { value: 'female', label: 'Female' }, { value: 'other', label: 'Other' },
];
const YES_NO = [
  { value: 'yes', label: 'Yes' }, { value: 'no', label: 'No' },
];
const YES_NO_UNSURE = [
  { value: 'yes', label: 'Yes' }, { value: 'no', label: 'No' }, { value: 'unsure', label: 'Unsure' },
];
const YES_NO_DISCUSS = [
  { value: 'yes', label: 'Yes' }, { value: 'no', label: 'No' }, { value: 'discuss_in_person', label: 'Prefer to discuss in person' },
];
const YES_SOMETIMES_NO = [
  { value: 'yes', label: 'Yes' }, { value: 'sometimes', label: 'Sometimes' }, { value: 'no', label: 'No' },
];
const GESTURE_OPTIONS = [
  { value: 'yes_often', label: 'Yes, often' }, { value: 'sometimes', label: 'Sometimes' },
  { value: 'rarely', label: 'Rarely' }, { value: 'no', label: 'No' },
];
const FACIAL_RESPONSE_OPTIONS = [
  { value: 'yes_usually', label: 'Yes, usually' }, { value: 'sometimes', label: 'Sometimes' },
  { value: 'rarely', label: 'Rarely' }, { value: 'no', label: 'No' },
];
const FREQUENCY_OPTIONS = [
  { value: 'never', label: 'Never' }, { value: 'occasionally', label: 'Occasionally' },
  { value: 'often', label: 'Often' }, { value: 'very_frequently', label: 'Very frequently' },
];
const YES_SOMEWHAT_NO = [
  { value: 'yes', label: 'Yes' }, { value: 'somewhat', label: 'Somewhat' }, { value: 'no', label: 'No' },
];
const CONSENT_OPTIONS = [
  { value: 'yes', label: 'Yes' }, { value: 'no', label: 'No' }, { value: 'discuss_first', label: 'Happy to discuss first' },
];
const READING_DIFFICULTIES = [
  { value: 'decoding', label: 'Sounding out / decoding words' },
  { value: 'fluency', label: 'Reading smoothly and fluently' },
  { value: 'speed', label: 'Reading speed' },
  { value: 'comprehension', label: 'Understanding what they read' },
  { value: 'no_reading_diff', label: 'No reading difficulties' },
  { value: 'too_early', label: 'Too early to tell' },
];
const WRITING_DIFFICULTIES = [
  { value: 'spelling', label: 'Spelling' },
  { value: 'organising_written', label: 'Organising written work' },
  { value: 'handwriting', label: 'Handwriting / letter formation' },
  { value: 'writing_stamina', label: 'Writing stamina / getting ideas down' },
  { value: 'no_writing_diff', label: 'No writing difficulties' },
  { value: 'too_early_w', label: 'Too early to tell' },
];
const MATHS_DIFFICULTIES = [
  { value: 'number_facts', label: 'Recalling number facts / times tables' },
  { value: 'number_concepts', label: 'Understanding number concepts' },
  { value: 'multi_step', label: 'Multi-step problems' },
  { value: 'time_money', label: 'Telling time / money' },
  { value: 'no_maths_diff', label: 'No maths difficulties' },
  { value: 'too_early_m', label: 'Too early to tell' },
];
const ATTENTION_SETTINGS = [
  { value: 'home', label: 'Home' }, { value: 'school', label: 'School' },
  { value: 'hobbies', label: 'Hobbies / activities' }, { value: 'friends', label: 'With friends' },
  { value: 'most_settings', label: 'Most or all settings' }, { value: 'mainly_one', label: 'Mainly one setting' },
];
const BOC_OPTIONS = [
  { value: 'aggression', label: 'Aggression toward others' },
  { value: 'property_damage', label: 'Property damage' },
  { value: 'absconding', label: 'Absconding or running' },
  { value: 'boc_none', label: 'None of these' },
  { value: 'boc_other', label: 'Other' },
];
const BOC_FREQ_OPTIONS = [
  { value: 'daily', label: 'Daily' }, { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' }, { value: 'less_often', label: 'Less often' },
];
const PROVIDER_OPTIONS = [
  { value: 'gp', label: 'GP' }, { value: 'paediatrician', label: 'Paediatrician' },
  { value: 'speech_path', label: 'Speech pathologist' }, { value: 'ot', label: 'Occupational therapist' },
  { value: 'psychologist', label: 'Psychologist' }, { value: 'provider_other', label: 'Other' },
];

// ── Styles ─────────────────────────────────────────────────────────
const sectionStyle = { marginBottom: 32, paddingBottom: 16, borderBottom: '1px solid #e5e7eb' };
const sectionTitle = { fontSize: '1.25rem', fontWeight: 700, color: '#1e3a5f', marginBottom: 4 };
const sectionSub = { fontSize: '0.8125rem', color: '#6b7280', marginBottom: 16 };
const fieldGap = { marginBottom: 16 };
const labelStyle = { fontSize: '0.875rem', fontWeight: 600, color: '#374151', marginBottom: 6, display: 'block' };
const optLabel = { fontSize: '0.8125rem', fontStyle: 'italic', color: '#9ca3af', marginLeft: 4 };
const inputStyle = {
  width: '100%', padding: '8px 12px', borderRadius: 8, border: '1px solid #d1d5db',
  fontSize: '0.875rem', fontFamily: 'inherit', color: '#1a202c',
};
const textareaStyle = { ...inputStyle, minHeight: 80, resize: 'vertical' };
const shortTextarea = { ...inputStyle, minHeight: 48, resize: 'vertical' };

// ── Component ──────────────────────────────────────────────────────
export default function NeuroDevPreForm({ responses, setResponses }) {
  const r = responses;
  const set = useCallback((key, val) => setResponses(prev => ({ ...prev, [key]: val })), [setResponses]);

  return (
    <div style={{ maxWidth: 800 }}>

      {/* ─── Section: Child Details ─── */}
      <div style={sectionStyle}>
        <h2 style={sectionTitle}>Child Details</h2>
        <div style={{ display: 'flex', gap: 16, ...fieldGap }}>
          <div style={{ flex: 1 }}>
            <label style={labelStyle}>Child's first name</label>
            <input type="text" style={inputStyle} value={r.child_first_name || ''} onChange={e => set('child_first_name', e.target.value)} data-testid="ndp-child-first-name" />
          </div>
          <div style={{ flex: 1 }}>
            <label style={labelStyle}>Child's last name / surname</label>
            <input type="text" style={inputStyle} value={r.child_last_name || ''} onChange={e => set('child_last_name', e.target.value)} data-testid="ndp-child-last-name" />
          </div>
        </div>
        <div style={{ display: 'flex', gap: 16, ...fieldGap }}>
          <div style={{ flex: 1 }}>
            <label style={labelStyle}>Child's date of birth</label>
            <DayFirstDate label="Date of birth" notFuture inputStyle={inputStyle} value={r.child_dob || ''} onChange={v => set('child_dob', v)} testId="ndp-child-dob" />
          </div>
          <div style={{ flex: 1 }}>
            <label style={labelStyle}>Today's date</label>
            <DayFirstDate label="Date" inputStyle={inputStyle} value={r.form_date || ''} onChange={v => set('form_date', v)} />
          </div>
        </div>
        <div style={fieldGap}>
          <ChipSelector label="Child's gender" options={GENDER_OPTIONS} value={r.child_gender || ''} onChange={v => set('child_gender', v)}
            revealField={{ value: 'other', placeholder: "Please specify..." }} revealValue={r.child_gender_other} onRevealChange={v => set('child_gender_other', v)} />
        </div>
        <div style={{ display: 'flex', gap: 16, ...fieldGap }}>
          <div style={{ flex: 1 }}>
            <label style={labelStyle}>Name your child prefers to be called</label>
            <input type="text" style={inputStyle} value={r.preferred_name || ''} onChange={e => set('preferred_name', e.target.value)} placeholder="If different from first name" />
          </div>
          <div style={{ flex: 1 }}>
            <label style={labelStyle}>Pronouns<span style={optLabel}>(optional)</span></label>
            <input type="text" style={inputStyle} value={r.pronouns || ''} onChange={e => set('pronouns', e.target.value)} placeholder="e.g. he/him, she/her, they/them" />
          </div>
        </div>
        <div style={fieldGap}>
          <label style={labelStyle}>Your relationship to the child</label>
          <input type="text" style={inputStyle} value={r.completer_relationship || ''} onChange={e => set('completer_relationship', e.target.value)} placeholder="e.g. Mother, Father, Carer, Grandparent" />
        </div>
        <div style={fieldGap}>
          <label style={labelStyle}>Languages spoken at home, and the child's main language</label>
          <textarea style={shortTextarea} value={r.languages || ''} onChange={e => set('languages', e.target.value)} placeholder="List languages spoken at home and which is the child's dominant language" data-testid="ndp-languages" />
        </div>
      </div>

      {/* ─── Section: About Your Child ─── */}
      <div style={sectionStyle}>
        <h2 style={sectionTitle}>About Your Child</h2>
        <div style={fieldGap}>
          <label style={labelStyle}>Tell us what you love about your child! We want to know everything there is to love about them here</label>
          <textarea style={{ ...textareaStyle, minHeight: 100 }} value={r.love_about_child || ''} onChange={e => set('love_about_child', e.target.value)} />
        </div>
      </div>

      {/* ─── Section: Family and Household (NEW) ─── */}
      <div style={sectionStyle}>
        <h2 style={sectionTitle}>Family and Household</h2>
        <div style={fieldGap}>
          <label style={labelStyle}>Who lives in the home?</label>
          <textarea style={shortTextarea} value={r.household || ''} onChange={e => set('household', e.target.value)} placeholder="e.g. Mum, Dad, two children" />
        </div>
        <div style={fieldGap}>
          <label style={labelStyle}>Siblings and ages</label>
          <textarea style={shortTextarea} value={r.siblings || ''} onChange={e => set('siblings', e.target.value)} placeholder="e.g. Older sister (10), younger brother (4)" />
        </div>
        <div style={fieldGap}>
          <label style={labelStyle}>Care or custody arrangements<span style={optLabel}>(optional)</span></label>
          <textarea style={shortTextarea} value={r.custody || ''} onChange={e => set('custody', e.target.value)} placeholder="e.g. Shared custody, lives with mum full-time" />
        </div>
        <div style={fieldGap}>
          <label style={labelStyle}>Cultural background or identity the family would like us to know about<span style={optLabel}>(optional)</span></label>
          <textarea style={shortTextarea} value={r.cultural_background || ''} onChange={e => set('cultural_background', e.target.value)} />
        </div>
      </div>

      {/* ─── Section: Pregnancy, Birth & Early Development ─── */}
      <div style={sectionStyle}>
        <h2 style={sectionTitle}>Pregnancy, Birth and Early Development</h2>
        <div style={fieldGap}>
          <label style={labelStyle}>Pregnancy and birth</label>
          <textarea style={textareaStyle} value={r.pregnancy_birth || ''} onChange={e => set('pregnancy_birth', e.target.value)} placeholder="Please describe the pregnancy and birth, including any complications (e.g. premature birth, low birth weight, distress during delivery, NICU stay)" />
        </div>
        <div style={fieldGap}>
          <label style={labelStyle}>Developmental milestones</label>
          <textarea style={textareaStyle} value={r.milestones || ''} onChange={e => set('milestones', e.target.value)} placeholder="At what age (in months) did your child reach these milestones, if known? Sitting, crawling, walking, first words, first sentences, toilet training" />
        </div>
        <div style={fieldGap}>
          <label style={labelStyle}>Were any milestones noticeably late, or did you have early concerns?</label>
          <textarea style={textareaStyle} value={r.milestone_concerns || ''} onChange={e => set('milestone_concerns', e.target.value)} />
        </div>
        <div style={fieldGap}>
          <label style={labelStyle}>Before your child could speak, how did they communicate their needs?</label>
          <textarea style={textareaStyle} value={r.pre_speech_communication || ''} onChange={e => set('pre_speech_communication', e.target.value)} placeholder="e.g. pointing, leading by the hand, crying, pulling you to things, reaching up arms to be picked up" />
        </div>
        <div style={fieldGap}>
          <ChipSelector label="Did your child ever lose or go backwards in skills they had already developed?" options={YES_NO} value={r.skill_regression || ''} onChange={v => set('skill_regression', v)} />
        </div>
        {r.skill_regression === 'yes' && (
          <div style={fieldGap}>
            <label style={labelStyle}>Please describe which skills were lost, at what age, and any triggering event</label>
            <textarea style={textareaStyle} value={r.skill_regression_details || ''} onChange={e => set('skill_regression_details', e.target.value)} />
          </div>
        )}
      </div>

      {/* ─── Section: Medical & Health ─── */}
      <div style={sectionStyle}>
        <h2 style={sectionTitle}>Medical and Health History</h2>
        <div style={fieldGap}>
          <label style={labelStyle}>Vision, hearing, seizures and medical conditions</label>
          <textarea style={textareaStyle} value={r.medical_history || ''} onChange={e => set('medical_history', e.target.value)} placeholder="When were vision and hearing last checked? Any history of seizures, tics, head injuries, or chronic conditions?" />
        </div>
        <div style={fieldGap}>
          <label style={labelStyle}>Sleep</label>
          <textarea style={textareaStyle} value={r.sleep || ''} onChange={e => set('sleep', e.target.value)} placeholder="Describe your child's sleep: any difficulty falling asleep, staying asleep, or waking early?" />
        </div>
        <div style={fieldGap}>
          <label style={labelStyle}>Current medications</label>
          <textarea style={shortTextarea} value={r.medications || ''} onChange={e => set('medications', e.target.value)} placeholder="Is your child currently taking any medications? If yes, please list" />
        </div>
      </div>

      {/* ─── Section: Previous Support & Diagnosis ─── */}
      <div style={sectionStyle}>
        <h2 style={sectionTitle}>Previous Support and Diagnosis</h2>
        <div style={fieldGap}>
          <ChipSelector label="Has your child received speech and language therapy?" options={YES_NO} value={r.speech_therapy || ''} onChange={v => set('speech_therapy', v)} />
        </div>
        {r.speech_therapy === 'yes' && (
          <div style={fieldGap}>
            <label style={labelStyle}>Speech therapy details</label>
            <textarea style={textareaStyle} value={r.speech_therapy_details || ''} onChange={e => set('speech_therapy_details', e.target.value)} placeholder="At what age did it begin, who referred them, and what was the focus?" />
          </div>
        )}
        <div style={fieldGap}>
          <ChipSelector label="Has your child had contact with early intervention, child development centres, or disability services?" options={YES_NO} value={r.early_intervention || ''} onChange={v => set('early_intervention', v)} />
        </div>
        {r.early_intervention === 'yes' && (
          <div style={fieldGap}>
            <label style={labelStyle}>Early intervention details</label>
            <textarea style={textareaStyle} value={r.early_intervention_details || ''} onChange={e => set('early_intervention_details', e.target.value)} />
          </div>
        )}
        <div style={fieldGap}>
          <ChipSelector label="Has your child previously been assessed or diagnosed with any neurodevelopmental condition?" options={YES_NO} value={r.previous_diagnosis || ''} onChange={v => set('previous_diagnosis', v)} />
        </div>
        {r.previous_diagnosis === 'yes' && (
          <div style={fieldGap}>
            <label style={labelStyle}>Diagnosis details</label>
            <textarea style={textareaStyle} value={r.previous_diagnosis_details || ''} onChange={e => set('previous_diagnosis_details', e.target.value)} placeholder="What was assessed or diagnosed, by whom, and when?" />
          </div>
        )}
        <div style={fieldGap}>
          <ChipSelector label="Are there previous assessment reports available?" options={YES_NO} value={r.reports_available || ''} onChange={v => set('reports_available', v)} />
        </div>
        <div style={fieldGap}>
          <label style={labelStyle}>Family history of neurodevelopmental conditions</label>
          <textarea style={textareaStyle} value={r.family_history || ''} onChange={e => set('family_history', e.target.value)} placeholder="Is there a family history of autism, ADHD, intellectual disability, tics, or other neurodevelopmental conditions?" />
        </div>
        <div style={fieldGap}>
          <label style={labelStyle}>Family history of mental health conditions</label>
          <textarea style={textareaStyle} value={r.family_history_mental_health || ''} onChange={e => set('family_history_mental_health', e.target.value)} placeholder="Is there a family history of depression, anxiety, bipolar disorder, or other mental health conditions?" />
        </div>
        <div style={fieldGap}>
          <label style={labelStyle}>Family history of learning disorders</label>
          <textarea style={textareaStyle} value={r.family_history_learning || ''} onChange={e => set('family_history_learning', e.target.value)} placeholder="Is there a family history of dyslexia, dyscalculia, or other specific learning disorders?" />
        </div>
        <div style={fieldGap}>
          <ChipSelector label="Consent to contact other providers" options={PROVIDER_OPTIONS} value={r.provider_consent || []} onChange={v => set('provider_consent', v)} multi />
        </div>
        {(r.provider_consent || []).length > 0 && (
          <div style={fieldGap}>
            <label style={labelStyle}>Provider names and contact details</label>
            <textarea style={textareaStyle} value={r.provider_details || ''} onChange={e => set('provider_details', e.target.value)} placeholder="Please list the names and contact details for each provider you have consented to us contacting" />
          </div>
        )}
      </div>

      {/* ─── Section: Education & Learning ─── */}
      <div style={sectionStyle}>
        <h2 style={sectionTitle}>Education and Learning</h2>
        <div style={fieldGap}>
          <label style={labelStyle}>Schools attended and current year level</label>
          <textarea style={shortTextarea} value={r.schools_year_level || ''} onChange={e => set('schools_year_level', e.target.value)} />
        </div>
        <div style={fieldGap}>
          <label style={labelStyle}>Overall, how does your child find school compared to peers their age?</label>
          <textarea style={textareaStyle} value={r.school_experience || ''} onChange={e => set('school_experience', e.target.value)} />
        </div>
        <div style={{ display: 'flex', gap: 16, ...fieldGap }}>
          <div style={{ flex: 1 }}>
            <label style={labelStyle}>Approximate days absent this year</label>
            <input type="text" style={inputStyle} value={r.days_absent || ''} onChange={e => set('days_absent', e.target.value)} placeholder="e.g. about 15 days" />
          </div>
          <div style={{ flex: 1 }}>
            <ChipSelector label="Any partial attendance, late starts, or early pickups?" options={YES_NO} value={r.partial_attendance || ''} onChange={v => set('partial_attendance', v)} />
          </div>
        </div>
        {r.partial_attendance === 'yes' && (
          <div style={fieldGap}>
            <label style={labelStyle}>Brief details</label>
            <textarea style={shortTextarea} value={r.partial_attendance_details || ''} onChange={e => set('partial_attendance_details', e.target.value)} />
          </div>
        )}
        <div style={fieldGap}>
          <ChipSelector label="READING: Does your child struggle with any of the following?" options={READING_DIFFICULTIES} value={r.reading_difficulties || []} onChange={v => set('reading_difficulties', v)} multi />
        </div>
        <div style={fieldGap}>
          <ChipSelector label="SPELLING & WRITING: Does your child struggle with any of the following?" options={WRITING_DIFFICULTIES} value={r.writing_difficulties || []} onChange={v => set('writing_difficulties', v)} multi />
        </div>
        <div style={fieldGap}>
          <ChipSelector label="MATHEMATICS: Does your child struggle with any of the following?" options={MATHS_DIFFICULTIES} value={r.maths_difficulties || []} onChange={v => set('maths_difficulties', v)} multi />
        </div>
        <div style={fieldGap}>
          <label style={labelStyle}>Does your child seem to 'know' material but struggle to get it onto paper?</label>
          <textarea style={textareaStyle} value={r.output_gap || ''} onChange={e => set('output_gap', e.target.value)} placeholder="Does your child find word-finding or formulation difficult?" />
        </div>
        <div style={fieldGap}>
          <label style={labelStyle}>Please describe any specific learning challenges in your own words</label>
          <textarea style={textareaStyle} value={r.learning_challenges || ''} onChange={e => set('learning_challenges', e.target.value)} placeholder="What challenges have you noticed, and roughly when did they first become apparent?" />
        </div>
        <div style={fieldGap}>
          <ChipSelector label="Does your child currently receive extra support at school?" options={YES_NO} value={r.school_support || ''} onChange={v => set('school_support', v)} />
        </div>
        {r.school_support === 'yes' && (
          <div style={fieldGap}>
            <label style={labelStyle}>School support details</label>
            <textarea style={textareaStyle} value={r.school_support_details || ''} onChange={e => set('school_support_details', e.target.value)} placeholder="Describe the support and how effective it has been" />
          </div>
        )}
        <div style={fieldGap}>
          <label style={labelStyle}>Previous learning interventions</label>
          <textarea style={textareaStyle} value={r.previous_interventions || ''} onChange={e => set('previous_interventions', e.target.value)} placeholder="Please list any previous tutoring, speech therapy, OT, psychology or other interventions. For learning-specific interventions (reading, spelling, maths, writing), note what was tried and how it helped." />
        </div>
      </div>

      {/* ─── Section: Social Communication & Play (TRIMMED) ─── */}
      <div style={sectionStyle}>
        <h2 style={sectionTitle}>Social Communication and Play</h2>
        <div style={fieldGap}>
          <label style={labelStyle}>Tell us about how your child communicates and connects with others</label>
          <textarea style={textareaStyle} value={r.communication_connection || ''} onChange={e => set('communication_connection', e.target.value)} placeholder="How do they use facial expressions, eye contact, and gestures? How do they interpret sarcasm, jokes, or implied meaning? How are they with unfamiliar people?" />
        </div>
        <div style={fieldGap}>
          <ChipSelector label="Does your child use gestures, pointing and showing objects to share interest?" options={GESTURE_OPTIONS} value={r.gesture_pointing || ''} onChange={v => set('gesture_pointing', v)} />
        </div>
        <div style={fieldGap}>
          <ChipSelector label="Does your child respond appropriately to your facial expressions and tone of voice?" options={FACIAL_RESPONSE_OPTIONS} value={r.facial_response || ''} onChange={v => set('facial_response', v)} />
        </div>
        <div style={fieldGap}>
          <label style={labelStyle}>Social interaction style</label>
          <textarea style={textareaStyle} value={r.social_interaction_style || ''} onChange={e => set('social_interaction_style', e.target.value)} placeholder="Does your child's social interaction feel 'different', for example focused mainly on their own needs or interests?" />
        </div>
        <div style={fieldGap}>
          <label style={labelStyle}>Friendships</label>
          <textarea style={textareaStyle} value={r.friendships || ''} onChange={e => set('friendships', e.target.value)} placeholder="How does your child form friendships? Do they seek connection or prefer solitary play? Describe the quality of their friendships. Are they maintained over time?" />
        </div>
        <div style={fieldGap}>
          <label style={labelStyle}>Quality of peer relationships</label>
          <textarea style={textareaStyle} value={r.peer_relationship_quality || ''} onChange={e => set('peer_relationship_quality', e.target.value)} placeholder="How does your child interact with peers their own age? Are relationships reciprocal? Do they tend to interact with younger or older children instead? Any concerns about bullying, isolation, or conflict?" />
        </div>
        <div style={fieldGap}>
          <label style={labelStyle}>Language patterns</label>
          <textarea style={textareaStyle} value={r.language_patterns || ''} onChange={e => set('language_patterns', e.target.value)} placeholder="Have you noticed repetitive phrases (echolalia), made-up words, or mixing up pronouns?" />
        </div>
        <div style={fieldGap}>
          <label style={labelStyle}>Play style</label>
          <textarea style={textareaStyle} value={r.play_style || ''} onChange={e => set('play_style', e.target.value)} placeholder="How does your child typically play with toys (e.g. imaginative / pretend play, lining up objects, focusing on parts)?" />
        </div>
        <div style={fieldGap}>
          <ChipSelector label="Does your child engage in pretend or imaginative play with others?" options={GESTURE_OPTIONS} value={r.pretend_play || ''} onChange={v => set('pretend_play', v)} />
        </div>
      </div>

      {/* ─── Section: Interests & Flexibility (TRIMMED) ─── */}
      <div style={sectionStyle}>
        <h2 style={sectionTitle}>Interests and Flexibility</h2>
        <div style={fieldGap}>
          <label style={labelStyle}>Intense interests</label>
          <textarea style={textareaStyle} value={r.intense_interests || ''} onChange={e => set('intense_interests', e.target.value)} placeholder="Describe your child's intense interests. How do they like to engage with or share them?" />
        </div>
        <div style={fieldGap}>
          <label style={labelStyle}>Routines, rituals, transitions and response to unexpected changes</label>
          <textarea style={textareaStyle} value={r.routines_and_changes || ''} onChange={e => set('routines_and_changes', e.target.value)} placeholder="Does your child have specific rituals or 'rules' they follow to feel safe? How do they handle transitions and unexpected changes or disappointments?" />
        </div>
      </div>

      {/* ─── Section: Attention & Activity (TRIMMED: removed duplicate free text) ─── */}
      <div style={sectionStyle}>
        <h2 style={sectionTitle}>Attention and Activity</h2>
        <div style={fieldGap}>
          <ChipSelector label="How often does your child have difficulty focusing on tasks or play activities?" options={FREQUENCY_OPTIONS} value={r.focus_difficulty || ''} onChange={v => set('focus_difficulty', v)} />
        </div>
        <div style={fieldGap}>
          <ChipSelector label="How often does your child feel 'driven by a motor' or seem unable to settle or relax?" options={FREQUENCY_OPTIONS} value={r.hyperactivity || ''} onChange={v => set('hyperactivity', v)} />
        </div>
        <div style={fieldGap}>
          <ChipSelector label="How often does your child act quickly on impulse, blurt out answers, or struggle to wait?" options={FREQUENCY_OPTIONS} value={r.impulsivity || ''} onChange={v => set('impulsivity', v)} />
        </div>
        <div style={fieldGap}>
          <label style={labelStyle}>At what age did these attention/activity differences first become noticeable?</label>
          <input type="text" style={inputStyle} value={r.attention_onset_age || ''} onChange={e => set('attention_onset_age', e.target.value)} placeholder="e.g. around age 4" />
        </div>
        <div style={fieldGap}>
          <ChipSelector label="In which settings do these differences occur?" options={ATTENTION_SETTINGS} value={r.attention_settings || []} onChange={v => set('attention_settings', v)} multi />
        </div>
        <div style={fieldGap}>
          <label style={labelStyle}>Organisation and executive function</label>
          <textarea style={textareaStyle} value={r.organisation_examples || ''} onChange={e => set('organisation_examples', e.target.value)} placeholder="Please give examples of difficulty with organisation, losing things, forgetfulness, or switching between tasks" />
        </div>
      </div>

      {/* ─── Section: Sensory & Motor ─── */}
      <div style={sectionStyle}>
        <h2 style={sectionTitle}>Sensory and Motor</h2>
        <div style={fieldGap}>
          <label style={labelStyle}>Sensory responses</label>
          <textarea style={textareaStyle} value={r.sensory_responses || ''} onChange={e => set('sensory_responses', e.target.value)} placeholder="Describe your child's responses to sounds, lights, textures, tastes or smells. Do they seek out or avoid particular sensory experiences?" />
        </div>
        <div style={fieldGap}>
          <ChipSelector label="Does your child use repetitive movements (e.g. rocking, hand-flapping, spinning) to regulate energy or emotions?" options={FREQUENCY_OPTIONS} value={r.repetitive_movements || ''} onChange={v => set('repetitive_movements', v)} />
        </div>
        <div style={fieldGap}>
          <ChipSelector label="GROSS MOTOR: Does your child seem clumsy, bump into things, or find running, catching or riding a bike difficult?" options={FREQUENCY_OPTIONS} value={r.gross_motor || ''} onChange={v => set('gross_motor', v)} />
        </div>
        <div style={fieldGap}>
          <ChipSelector label="FINE MOTOR: Does your child find handwriting, using scissors, doing up buttons or tying laces difficult?" options={FREQUENCY_OPTIONS} value={r.fine_motor || ''} onChange={v => set('fine_motor', v)} />
        </div>
        <div style={fieldGap}>
          <label style={labelStyle}>Additional movement, coordination, or sensory notes</label>
          <textarea style={textareaStyle} value={r.motor_sensory_notes || ''} onChange={e => set('motor_sensory_notes', e.target.value)} />
        </div>
      </div>

      {/* ─── Section: Emotions & Regulation ─── */}
      <div style={sectionStyle}>
        <h2 style={sectionTitle}>Emotions and Regulation</h2>
        <div style={fieldGap}>
          <ChipSelector label="Does your child have difficulty understanding or identifying their own emotions?" options={YES_SOMETIMES_NO} value={r.emotion_identification || ''} onChange={v => set('emotion_identification', v)} />
        </div>
        <div style={fieldGap}>
          <ChipSelector label="Does your child have difficulty managing or regulating emotions once they become upset?" options={YES_SOMETIMES_NO} value={r.emotion_regulation || ''} onChange={v => set('emotion_regulation', v)} />
        </div>
        <div style={fieldGap}>
          <label style={labelStyle}>How does your child typically express and manage strong emotions?</label>
          <textarea style={textareaStyle} value={r.emotion_expression || ''} onChange={e => set('emotion_expression', e.target.value)} placeholder="Describe how they handle anger, frustration, anxiety, sadness" />
        </div>
        <div style={fieldGap}>
          <label style={labelStyle}>Anxiety, low mood, or emotional outbursts</label>
          <textarea style={textareaStyle} value={r.anxiety_mood || ''} onChange={e => set('anxiety_mood', e.target.value)} placeholder="Has your child experienced anxiety, low mood, or frequent emotional outbursts? Please describe" />
        </div>
        <div style={fieldGap}>
          <label style={labelStyle}>Masking or delayed emotional release</label>
          <textarea style={textareaStyle} value={r.masking_release || ''} onChange={e => set('masking_release', e.target.value)} placeholder="Does your child hold things together at school and then have a big emotional release at home (or vice versa)?" />
        </div>
      </div>

      {/* ─── Section: Daily Living & Independence (EXTENDED) ─── */}
      <div style={sectionStyle}>
        <h2 style={sectionTitle}>Daily Living and Independence</h2>
        <div style={fieldGap}>
          <label style={labelStyle}>Self-care</label>
          <textarea style={textareaStyle} value={r.self_care || ''} onChange={e => set('self_care', e.target.value)} placeholder="How does your child manage age-appropriate self-care (dressing, hygiene, eating, sleep routines)?" />
        </div>
        <div style={fieldGap}>
          <label style={labelStyle}>Independence and routines</label>
          <textarea style={textareaStyle} value={r.independence_routines || ''} onChange={e => set('independence_routines', e.target.value)} placeholder="Can your child follow daily routines and instructions, and complete age-appropriate tasks independently?" />
        </div>
        <div style={fieldGap}>
          <label style={labelStyle}>Safety and money awareness</label>
          <textarea style={textareaStyle} value={r.safety_money || ''} onChange={e => set('safety_money', e.target.value)} placeholder="How is your child's awareness of everyday safety, road sense, and (if relevant) handling of money?" />
        </div>
        <div style={fieldGap}>
          <ChipSelector label="Continence: any daytime or nighttime accidents, soiling, or toileting avoidance?" options={YES_NO} value={r.continence || ''} onChange={v => set('continence', v)} />
        </div>
        {r.continence === 'yes' && (
          <div style={fieldGap}>
            <label style={labelStyle}>Brief details</label>
            <textarea style={shortTextarea} value={r.continence_details || ''} onChange={e => set('continence_details', e.target.value)} />
          </div>
        )}
        <div style={fieldGap}>
          <ChipSelector label="Any growth or nutrition concerns, or dietitian involvement?" options={YES_NO} value={r.nutrition || ''} onChange={v => set('nutrition', v)} />
        </div>
        {r.nutrition === 'yes' && (
          <div style={fieldGap}>
            <label style={labelStyle}>Brief details</label>
            <textarea style={shortTextarea} value={r.nutrition_details || ''} onChange={e => set('nutrition_details', e.target.value)} />
          </div>
        )}
        <div style={fieldGap}>
          <ChipSelector label="Compared with other children the same age, are there everyday tasks your child needs noticeably more help with?" options={YES_SOMEWHAT_NO} value={r.needs_more_help || ''} onChange={v => set('needs_more_help', v)} />
        </div>
        {(r.needs_more_help === 'yes' || r.needs_more_help === 'somewhat') && (
          <div style={fieldGap}>
            <label style={labelStyle}>Please give examples</label>
            <textarea style={textareaStyle} value={r.needs_more_help_examples || ''} onChange={e => set('needs_more_help_examples', e.target.value)} />
          </div>
        )}
      </div>

      {/* ─── Section: Strengths & Impact ─── */}
      <div style={sectionStyle}>
        <h2 style={sectionTitle}>Strengths and Impact</h2>
        <div style={fieldGap}>
          <label style={labelStyle}>What are your child's strengths, talents and favourite activities?</label>
          <textarea style={textareaStyle} value={r.strengths || ''} onChange={e => set('strengths', e.target.value)} />
        </div>
        <div style={fieldGap}>
          <label style={labelStyle}>When is your child at their happiest or most settled? What helps them do their best?</label>
          <textarea style={textareaStyle} value={r.happiest_settled || ''} onChange={e => set('happiest_settled', e.target.value)} />
        </div>
        <div style={fieldGap}>
          <label style={labelStyle}>How do these challenges currently affect your child's everyday life?</label>
          <textarea style={textareaStyle} value={r.daily_impact || ''} onChange={e => set('daily_impact', e.target.value)} placeholder="At home, at school, and with friends" />
        </div>
        <div style={fieldGap}>
          <label style={labelStyle}>How have these challenges affected your child's confidence or self-esteem?</label>
          <textarea style={textareaStyle} value={r.confidence_self_esteem || ''} onChange={e => set('confidence_self_esteem', e.target.value)} />
        </div>
      </div>

      {/* ─── Section: Safety & Wellbeing (EXTENDED) ─── */}
      <div style={sectionStyle}>
        <h2 style={sectionTitle}>Safety and Wellbeing</h2>
        <div style={fieldGap}>
          <ChipSelector label="Has your child ever engaged in self-harming behaviour or expressed thoughts of harming themselves?" options={YES_NO} value={r.self_harm || ''} onChange={v => set('self_harm', v)} />
        </div>
        {r.self_harm === 'yes' && (
          <div style={{ ...fieldGap, padding: 12, borderRadius: 8, border: '1px solid #fca5a5', backgroundColor: '#fef2f2' }}>
            <label style={{ ...labelStyle, color: '#991b1b' }}>Please provide details</label>
            <textarea style={{ ...textareaStyle, borderColor: '#fca5a5' }} value={r.self_harm_details || ''} onChange={e => set('self_harm_details', e.target.value)} placeholder="This information is important for your child's safety and will be handled with care" />
          </div>
        )}
        <div style={fieldGap}>
          <ChipSelector label="Behaviours of concern" options={BOC_OPTIONS} value={r.behaviours_of_concern || []} onChange={v => set('behaviours_of_concern', v)} multi />
        </div>
        {(r.behaviours_of_concern || []).length > 0 && !(r.behaviours_of_concern || []).every(v => v === 'boc_none') && (
          <>
            <div style={fieldGap}>
              <ChipSelector label="How often do these occur?" options={BOC_FREQ_OPTIONS} value={r.boc_frequency || ''} onChange={v => set('boc_frequency', v)} />
            </div>
            <div style={fieldGap}>
              <label style={labelStyle}>Brief details</label>
              <textarea style={shortTextarea} value={r.boc_details || ''} onChange={e => set('boc_details', e.target.value)} />
            </div>
          </>
        )}
        <div style={fieldGap}>
          <label style={labelStyle}>Screen time</label>
          <textarea style={shortTextarea} value={r.screen_time || ''} onChange={e => set('screen_time', e.target.value)} placeholder="Approximate daily hours, and any difficulty stopping or transitioning off screens" />
        </div>
      </div>

      {/* ─── Section: Significant Life Events ─── */}
      <div style={sectionStyle}>
        <h2 style={sectionTitle}>Significant Life Events</h2>
        <div style={fieldGap}>
          <label style={labelStyle}>Please share any significant life events that may be relevant</label>
          <textarea style={{ ...textareaStyle, minHeight: 100 }} value={r.significant_life_events || ''} onChange={e => set('significant_life_events', e.target.value)} />
        </div>
      </div>

      {/* ─── Section: Your Concerns & Goals (EXTENDED) ─── */}
      <div style={sectionStyle}>
        <h2 style={sectionTitle}>Your Concerns and Goals</h2>
        <div style={fieldGap}>
          <label style={labelStyle}>What has your child been told about this assessment?</label>
          <textarea style={shortTextarea} value={r.child_told || ''} onChange={e => set('child_told', e.target.value)} />
        </div>
        <div style={fieldGap}>
          <label style={labelStyle}>What are your main concerns or reasons for seeking this assessment?</label>
          <textarea style={{ ...textareaStyle, minHeight: 100 }} value={r.main_concerns || ''} onChange={e => set('main_concerns', e.target.value)} />
        </div>
        <div style={fieldGap}>
          <label style={labelStyle}>What are you hoping this assessment will help clarify or achieve?</label>
          <textarea style={{ ...textareaStyle, minHeight: 100 }} value={r.assessment_hopes || ''} onChange={e => set('assessment_hopes', e.target.value)} />
        </div>
        <div style={fieldGap}>
          <label style={labelStyle}>Is there anything else you feel we should know about your child?</label>
          <textarea style={textareaStyle} value={r.anything_else || ''} onChange={e => set('anything_else', e.target.value)} />
        </div>
      </div>

      {/* ─── Section: School Contact Consent ─── */}
      <div style={{ marginBottom: 16 }}>
        <h2 style={sectionTitle}>School Contact Consent</h2>
        <p style={sectionSub}>With your consent, we may contact your child's school or teacher for a brief report, as information across settings greatly strengthens the assessment.</p>
        <div style={fieldGap}>
          <ChipSelector label="Do you consent to us contacting your child's school?" options={CONSENT_OPTIONS} value={r.school_contact_consent || ''} onChange={v => set('school_contact_consent', v)} />
        </div>
      </div>

    </div>
  );
}
